// Carrega as agendas de recebível de TODA a carteira para a tela.
//
// Existe para que a Previsão de Recebíveis (operacional), o Painel Executivo, o
// export do Excel e agora o catálogo da Vick leiam exatamente o mesmo número.
// Duas buscas separadas divergiriam por motivos bobos e invisíveis — esquecer a
// paginação de 1.000 linhas do PostgREST, esquecer `deduplicarUcs` (208
// cadastros duplicados, 4,7% da carteira contados em dobro), ou filtrar UC ativa
// de um jeito diferente — e aí a diretoria e o operacional discutem qual tela
// está certa.
//
// A busca em si mora em `supabase/functions/_shared/carteiraRecebiveis.ts`,
// porque a edge function da Vick precisa da mesma conta e não alcança `src/`.
// Aqui sobra só o que é de React: estado, ciclo de vida e erro na tela.

import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import {
  buscarAgendasRecebiveis as buscarAgendas,
  type DadosRecebiveis,
} from '../../supabase/functions/_shared/carteiraRecebiveis.ts';

export {
  MESES_HISTORICO,
  COLUNAS_UC_RECEBIVEL,
  inicioDaJanela,
} from '../../supabase/functions/_shared/carteiraRecebiveis.ts';
export type { DadosRecebiveis } from '../../supabase/functions/_shared/carteiraRecebiveis.ts';

export interface UseAgendasRecebiveis extends Partial<DadosRecebiveis> {
  loading: boolean;
  carregouUmaVez: boolean;
  erro: string | null;
  recarregar: () => void;
}

/**
 * @param ativo `false` adia a busca — a carteira inteira é pesada (todas as UCs
 *   e 15 meses de faturas), e o Painel Executivo já carrega quatro seções na
 *   mesma montagem. Permite carregar só quando a seção entra em cena.
 * @param horizonteMeses quantos meses projetar à frente.
 */
export function useAgendasRecebiveis({
  ativo = true,
  horizonteMeses = 6,
}: { ativo?: boolean; horizonteMeses?: number } = {}): UseAgendasRecebiveis {
  const [dados, setDados] = useState<DadosRecebiveis | null>(null);
  const [loading, setLoading] = useState(false);
  const [carregouUmaVez, setCarregouUmaVez] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  const carregar = useCallback(async () => {
    setLoading(true);
    setErro(null);
    try {
      setDados(await buscarAgendas(supabase, { horizonteMeses }));
    } catch (e: any) {
      console.error('Erro ao montar as agendas de recebíveis:', e);
      setErro(e?.message || 'Não foi possível carregar a previsão.');
    } finally {
      setLoading(false);
      setCarregouUmaVez(true);
    }
  }, [horizonteMeses]);

  useEffect(() => {
    if (ativo) carregar();
  }, [ativo, carregar]);

  return { ...(dados ?? {}), loading, carregouUmaVez, erro, recarregar: carregar };
}

/**
 * A busca sem React, para quem precisa dela fora de componente (o export do
 * Excel). Amarra o client do navegador — a versão agnóstica está em `_shared`.
 */
export function buscarAgendasRecebiveis(
  opcoes: { horizonteMeses?: number } = {},
): Promise<DadosRecebiveis> {
  return buscarAgendas(supabase, opcoes);
}
